import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Building2, Search, MapPin, Bed, Bath, Calendar,
  Filter, ArrowLeft, Send, Check, X, Clock, FileText,
  ChevronRight, Home, PoundSterling, MessageSquare
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { marketplaceAPI, messagesAPI } from '../services/api';
import ListingDetailModal from '../components/ListingDetailModal';
import R2RDealBreakdown from '../components/R2RDealBreakdown';

const HostDealMarketplace = () => {
  const navigate = useNavigate();
  const { user, getAuthToken, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [listings, setListings] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerData, setOfferData] = useState({ proposedRent: 0, proposedTerm: 12, message: '' });
  const [detailListing, setDetailListing] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [payingOfferId, setPayingOfferId] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    city: '',
    minRent: '',
    maxRent: '',
    bedrooms: ''
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    if (!user?.roles?.includes('host')) {
      toast({ 
        title: 'Access Denied', 
        description: 'You must be a host to access the deal marketplace',
        variant: 'destructive'
      });
      navigate('/');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, user, navigate]);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const [listingsData, offersData] = await Promise.all([
        marketplaceAPI.browseListings({}, token),
        marketplaceAPI.getSentOffers(token)
      ]);
      setListings(listingsData);
      setMyOffers(offersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to load marketplace', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const searchFilters = {};
      if (filters.city) searchFilters.city = filters.city;
      if (filters.minRent) searchFilters.min_rent = parseFloat(filters.minRent);
      if (filters.maxRent) searchFilters.max_rent = parseFloat(filters.maxRent);
      if (filters.bedrooms) searchFilters.bedrooms = parseInt(filters.bedrooms);
      
      const results = await marketplaceAPI.browseListings(searchFilters, token);
      setListings(results);
    } catch (error) {
      toast({ title: 'Error', description: 'Search failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleMakeOffer = (listing) => {
    setSelectedListing(listing);
    setOfferData({ 
      proposedRent: listing.monthlyRent, 
      proposedTerm: listing.minTerm || 12, 
      message: '' 
    });
    setShowOfferModal(true);
  };

  const handleSubmitOffer = async () => {
    try {
      const token = getAuthToken();
      await marketplaceAPI.createOffer({
        rentalListingId: selectedListing._id,
        proposedRent: offerData.proposedRent,
        proposedTerm: offerData.proposedTerm,
        message: offerData.message
      }, token);
      toast({ title: 'Success', description: 'Offer submitted!' });
      setShowOfferModal(false);
      setSelectedListing(null);
      fetchData();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to submit offer', 
        variant: 'destructive' 
      });
    }
  };

  const handleWithdrawOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to withdraw this offer?')) return;
    try {
      const token = getAuthToken();
      await marketplaceAPI.withdrawOffer(offerId, token);
      toast({ title: 'Offer Withdrawn' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to withdraw offer', variant: 'destructive' });
    }
  };

  // The one payment that concludes an R2R deal: the rent agreed with the
  // landlord plus Ariv's 2%, charged once. Until it succeeds no fee is
  // earned, no ledger entry exists and no referral is owed — which is why
  // the deal is only real at this point, not at acceptance.
  const handlePayDeal = async (offerId) => {
    setCheckoutLoading(true);
    try {
      const token = getAuthToken();
      const { url } = await marketplaceAPI.payDeal(offerId, window.location.origin, token);
      if (url) {
        window.location.href = url;
      } else {
        toast({ title: 'Error', description: 'Could not open the payment page', variant: 'destructive' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not start the payment',
        variant: 'destructive',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleConvertToProperty = async (offerId) => {
    try {
      const token = getAuthToken();
      const result = await marketplaceAPI.convertToProperty(offerId, {}, token);
      toast({ title: 'Success', description: 'Property created from deal!' });
      navigate(`/host/edit-property/${result.property._id}`);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to convert', variant: 'destructive' });
    }
  };

  const handleDetailMakeOffer = (listing) => {
    setDetailListing(null);
    handleMakeOffer(listing);
  };

  const hasOfferOnListing = (listingId) => {
    return myOffers.some(o => o.rentalListingId === listingId && o.status === 'pending');
  };

  const getOfferStatus = (status) => {
    switch (status) {
      case 'pending': return { color: 'bg-blue-100 text-blue-800', icon: Clock };
      case 'accepted': return { color: 'bg-green-100 text-green-800', icon: Check };
      case 'rejected': return { color: 'bg-red-100 text-red-800', icon: X };
      case 'withdrawn': return { color: 'bg-gray-100 text-gray-800', icon: X };
      default: return { color: 'bg-gray-100 text-gray-800', icon: FileText };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading marketplace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/host/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-[#1F3A68]">Deal Marketplace</h1>
                <p className="text-sm text-gray-500">Find R2R opportunities</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">My Offers: {myOffers.length}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Non-circumvention notice — surfaced at the moment a host is about
            to engage with a landlord, so they cannot reasonably claim they
            were unaware of the off-platform rules. See T&Cs Section 13A. */}
        <div className="mb-6 rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
          <p className="text-amber-900 font-bold uppercase tracking-wide text-xs mb-1">
            ⚠ Keep this relationship on Ariv
          </p>
          <p className="text-amber-950 text-sm leading-relaxed">
            If You meet a Landlord here, You must negotiate, agree, and pay through Ariv. Going off-platform to bypass our fees, even months later, breaches our Terms and exposes You to account termination plus the avoided fee plus a £500 administrative charge per breach. Same rule applies to Landlords approaching You.{' '}
            <a
              href="/terms#non-circumvention"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold hover:text-amber-700"
            >
              Read the full clause (Section 13A)
            </a>
          </p>
        </div>

        {/* Search Filters */}
        <Card className="mb-8">
          <CardContent className="p-4">
            <div className="grid md:grid-cols-5 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">City</label>
                <Input 
                  placeholder="e.g. London"
                  value={filters.city}
                  onChange={(e) => setFilters({...filters, city: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Min Rent (£)</label>
                <Input 
                  type="number"
                  placeholder="500"
                  value={filters.minRent}
                  onChange={(e) => setFilters({...filters, minRent: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Max Rent (£)</label>
                <Input 
                  type="number"
                  placeholder="2000"
                  value={filters.maxRent}
                  onChange={(e) => setFilters({...filters, maxRent: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Bedrooms</label>
                <select 
                  className="w-full h-10 border rounded-md px-3"
                  value={filters.bedrooms}
                  onChange={(e) => setFilters({...filters, bedrooms: e.target.value})}
                >
                  <option value="">Any</option>
                  <option value="0">Studio</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3+</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button 
                  className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  onClick={handleSearch}
                >
                  <Search size={16} className="mr-1" /> Search
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* My Offers Section */}
        {myOffers.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1F3A68] mb-4">My Offers</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myOffers.map(offer => {
                const statusInfo = getOfferStatus(offer.status);
                const StatusIcon = statusInfo.icon;
                return (
                  <Card
                    key={offer._id}
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => setSelectedOffer(offer)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{offer.listingAddress}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.color}`}>
                          <StatusIcon size={12} /> {offer.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{offer.listingCity}</p>
                      <div className="flex justify-between text-sm mb-3">
                        <span>Proposed: £{offer.proposedRent}/mo</span>
                        <span>{offer.proposedTerm} months</span>
                      </div>
                      <div className="flex gap-2">
                        {offer.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={(e) => { e.stopPropagation(); handleWithdrawOffer(offer._id); }}
                          >
                            Withdraw
                          </Button>
                        )}
                        {offer.status === 'accepted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); setPayingOfferId(payingOfferId === offer._id ? null : offer._id); }}
                          >
                            {payingOfferId === offer._id ? 'Hide costs' : 'Conclude deal'}
                          </Button>
                        )}
                        {offer.status === 'accepted' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={(e) => { e.stopPropagation(); handleConvertToProperty(offer._id); }}
                          >
                            Convert to Property
                          </Button>
                        )}
                        <span className="ml-auto text-gray-400 flex items-center text-xs">
                          View details <ChevronRight size={14} />
                        </span>
                      </div>
                      {payingOfferId === offer._id && (
                        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                          <R2RDealBreakdown
                            offerId={offer._id}
                            role="host"
                            token={getAuthToken()}
                            paying={checkoutLoading}
                            onPay={() => handlePayDeal(offer._id)}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Listings Grid */}
        <h2 className="text-xl font-bold text-[#1F3A68] mb-4">Available Properties ({listings.length})</h2>
        {listings.length === 0 ? (
          <Card className="p-8 text-center">
            <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">No listings found</h3>
            <p className="text-gray-500">Try adjusting your search filters</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map(listing => {
              const hasOffer = hasOfferOnListing(listing._id);
              return (
                <ListingCard
                  key={listing._id}
                  listing={listing}
                  onMakeOffer={() => handleMakeOffer(listing)}
                  hasOffer={hasOffer}
                  onClick={() => {
                    // If the host already has an offer on this listing, open
                    // the offer detail panel directly so they see their
                    // proposal status (not the listing detail again).
                    if (hasOffer) {
                      const existing = myOffers.find(o => o.rentalListingId === listing._id && o.status === 'pending');
                      if (existing) {
                        setSelectedOffer(existing);
                        return;
                      }
                    }
                    setDetailListing(listing);
                  }}
                />
              );
            })}
          </div>
        )}
      </main>

      {/* Listing Detail Modal */}
      {detailListing && (
        <ListingDetailModal
          listing={detailListing}
          mode="marketplace"
          onClose={() => setDetailListing(null)}
          onMakeOffer={handleDetailMakeOffer}
          hasOffer={hasOfferOnListing(detailListing._id)}
        />
      )}

      {/* Offer Detail Panel */}
      {selectedOffer && (
        <OfferDetailPanel
          offer={selectedOffer}
          getOfferStatus={getOfferStatus}
          onClose={() => setSelectedOffer(null)}
          onWithdraw={(id) => { setSelectedOffer(null); handleWithdrawOffer(id); }}
          onConvert={(id) => { setSelectedOffer(null); handleConvertToProperty(id); }}
        />
      )}

      {/* Make Offer Modal */}
      {showOfferModal && selectedListing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Make an Offer</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowOfferModal(false)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <h3 className="font-semibold">{selectedListing.address}</h3>
                <p className="text-sm text-gray-600">{selectedListing.city}</p>
                <p className="text-lg font-bold text-[#1F3A68] mt-1">
                  Listed: £{selectedListing.monthlyRent}/mo
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Your Proposed Rent (£/month)</label>
                <Input 
                  type="number"
                  value={offerData.proposedRent}
                  onChange={(e) => setOfferData({...offerData, proposedRent: parseFloat(e.target.value)})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Proposed Term (months)</label>
                <Input 
                  type="number"
                  min="1"
                  value={offerData.proposedTerm}
                  onChange={(e) => setOfferData({...offerData, proposedTerm: parseInt(e.target.value)})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Message to Landlord</label>
                <Textarea 
                  value={offerData.message}
                  onChange={(e) => setOfferData({...offerData, message: e.target.value})}
                  placeholder="Introduce yourself and explain your proposal..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  className="flex-1 bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  onClick={handleSubmitOffer}
                >
                  <Send size={16} className="mr-1" /> Submit Offer
                </Button>
                <Button variant="outline" onClick={() => setShowOfferModal(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Offer Detail Panel
const OfferDetailPanel = ({ offer, getOfferStatus, onClose, onWithdraw, onConvert }) => {
  const { getAuthToken, user } = useAuth();
  const { toast } = useToast();
  const statusInfo = getOfferStatus(offer.status);
  const StatusIcon = statusInfo.icon;
  const savingPerMonth = offer.originalRent ? offer.originalRent - offer.proposedRent : null;
  const currentUserId = user?._id || user?.id;

  // Deal chat state
  const [showChat, setShowChat] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const chatPollRef = useRef(null);
  const chatEndRef = useRef(null);

  const fetchDealConversation = async () => {
    try {
      const token = getAuthToken();
      const convs = await messagesAPI.getConversations(token, 'guest');
      // Exclude self-conversations (stale data) to avoid matching the wrong conversation
      const match = convs.find(c =>
        c.conversationType === 'deal' &&
        c.relatedId === offer.rentalListingId &&
        String(c.hostId) !== String(c.guestId)
      );
      if (match) {
        setConversation(match);
        const data = await messagesAPI.getConversation(match._id, token);
        setChatMessages(data.messages || []);
        return match;
      }
      return null;
    } catch (err) {
      console.error('fetchDealConversation error', err);
      return null;
    }
  };

  const handleOpenChat = async () => {
    setShowChat(true);
    setChatLoading(true);
    await fetchDealConversation();
    setChatLoading(false);
    // Always start polling — discovers new conversations even if none exists yet
    if (!chatPollRef.current) {
      chatPollRef.current = setInterval(fetchDealConversation, 5000);
    }
  };

  // Cleanup polling on unmount / close
  useEffect(() => {
    return () => clearInterval(chatPollRef.current);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;
    setChatSending(true);
    const msgText = chatInput.trim();
    setChatInput('');
    try {
      const token = getAuthToken();
      if (!conversation) {
        // First message — create deal conversation
        const conv = await messagesAPI.createConversation({
          participantId: offer.landlordId,
          conversationType: 'deal',
          participantType: 'landlord',
          initialMessage: msgText,
          relatedId: offer.rentalListingId,
        }, token);
        setConversation(conv);
        const data = await messagesAPI.getConversation(conv._id, token);
        setChatMessages(data.messages || []);
      } else {
        const msg = await messagesAPI.sendMessage({
          conversationId: conversation._id,
          content: msgText,
        }, token);
        setChatMessages(prev => [...prev, msg]);
      }
    } catch (err) {
      setChatInput(msgText);
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setChatSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose}>
      <div
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-[#F5F0E8] shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b z-10 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h2 className="font-bold text-[#1F3A68] text-lg leading-tight">{offer.listingAddress}</h2>
              <p className="text-gray-500 text-sm flex items-center gap-1">
                <MapPin size={12} /> {offer.listingCity}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${statusInfo.color}`}>
            <StatusIcon size={13} />
            {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
          </span>
        </div>

        {showChat ? (
          /* ── Inline Chat View ── */
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Chat sub-header */}
            <div className="px-4 py-2.5 border-b bg-[#1F3A68]/5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare size={15} className="text-[#1F3A68]" />
                <span className="text-sm font-semibold text-[#1F3A68]">Negotiation Chat</span>
                {offer.landlordName && (
                  <span className="text-xs text-gray-500">with {offer.landlordName}</span>
                )}
              </div>
              <button
                onClick={() => { setShowChat(false); clearInterval(chatPollRef.current); chatPollRef.current = null; }}
                className="text-xs text-gray-500 hover:text-[#1F3A68] flex items-center gap-1"
              >
                <ArrowLeft size={13} /> Back to details
              </button>
            </div>

            {/* Messages list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatLoading && (
                <div className="text-center text-gray-400 text-sm py-8">Loading…</div>
              )}
              {!chatLoading && chatMessages.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare size={40} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">No messages yet</p>
                  <p className="text-gray-400 text-xs mt-1">Send a message to negotiate the deal</p>
                </div>
              )}
              {chatMessages.map((msg) => {
                const isOwn = msg.senderId === currentUserId;
                return (
                  <div key={msg._id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                      isOwn
                        ? 'bg-[#1F3A68] text-white rounded-tr-sm'
                        : 'bg-white text-gray-800 rounded-tl-sm shadow-sm'
                    }`}>
                      {!isOwn && (
                        <p className="text-xs font-semibold text-[#1F3A68] mb-1">{msg.senderName}</p>
                      )}
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${isOwn ? 'text-white/60' : 'text-gray-400'}`}>
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Input area */}
            <div className="border-t p-3 flex gap-2 bg-white flex-shrink-0">
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChatMessage(); } }}
                placeholder="Type a message to negotiate…"
                disabled={chatSending}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
              />
              <Button
                size="sm"
                className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 px-3"
                onClick={handleSendChatMessage}
                disabled={chatSending || !chatInput.trim()}
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        ) : (
          /* ── Details View ── */
          <div className="flex-1 overflow-y-auto p-6 space-y-5">

            {/* Status Banner */}
            {offer.status === 'accepted' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <Check size={20} className="text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Offer Accepted! 🎉</p>
                  <p className="text-sm text-green-700 mt-0.5">The landlord has accepted your offer. You can now convert this deal into a managed property.</p>
                </div>
              </div>
            )}
            {offer.status === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <X size={20} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-red-700">Offer Rejected</p>
                  <p className="text-sm text-red-600 mt-0.5">The landlord has declined this offer. You may browse other listings and make a new offer.</p>
                </div>
              </div>
            )}
            {offer.status === 'pending' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <Clock size={20} className="text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-blue-800">Awaiting Landlord Response</p>
                  <p className="text-sm text-blue-700 mt-0.5">Your offer is under review. You'll be notified once the landlord responds.</p>
                </div>
              </div>
            )}

            {/* Financials */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-[#1F3A68] mb-4 flex items-center gap-2">
                <PoundSterling size={16} /> Financial Summary
              </h3>
              <div className="space-y-3">
                {offer.originalRent && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">Listed Rent</span>
                    <span className="font-semibold text-gray-800">£{offer.originalRent.toLocaleString()}/mo</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 text-sm">Your Proposed Rent</span>
                  <span className="font-bold text-[#1F3A68] text-lg">£{offer.proposedRent.toLocaleString()}/mo</span>
                </div>
                {savingPerMonth !== null && savingPerMonth > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">Monthly Saving</span>
                    <span className="font-semibold text-green-600">−£{savingPerMonth.toLocaleString()}/mo</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 text-sm">Proposed Term</span>
                  <span className="font-semibold text-gray-800">{offer.proposedTerm} months</span>
                </div>
                {offer.proposedTerm && offer.proposedRent && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 text-sm">Total Contract Value</span>
                    <span className="font-semibold text-gray-800">
                      £{(offer.proposedRent * offer.proposedTerm).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Your Message + Continue Messaging */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#1F3A68] flex items-center gap-2">
                  <FileText size={16} /> Your Message to Landlord
                </h3>
                <Button
                  size="sm"
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 text-xs px-3 h-7"
                  onClick={handleOpenChat}
                >
                  <MessageSquare size={13} className="mr-1.5" />
                  Continue Messaging
                </Button>
              </div>
              {offer.message && (
                <p className="text-gray-700 text-sm leading-relaxed bg-gray-50 rounded-lg p-3 italic">
                  "{offer.message}"
                </p>
              )}
              {!offer.message && (
                <p className="text-gray-400 text-sm italic">No initial message was sent with this offer.</p>
              )}
            </div>

            {/* Offer Details */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-[#1F3A68] mb-4 flex items-center gap-2">
                <Building2 size={16} /> Offer Details
              </h3>
              <div className="space-y-2 text-sm">
                {offer.createdAt && (
                  <div className="flex justify-between py-1.5 border-b border-gray-100">
                    <span className="text-gray-500">Submitted</span>
                    <span className="text-gray-800 font-medium">
                      {new Date(offer.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                )}
                {offer.updatedAt && offer.status !== 'pending' && (
                  <div className="flex justify-between py-1.5 border-b border-gray-100">
                    <span className="text-gray-500">Responded</span>
                    <span className="text-gray-800 font-medium">
                      {new Date(offer.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Status</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-2">
              {offer.status === 'accepted' && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 py-3 text-base font-semibold"
                  onClick={() => onConvert(offer._id)}
                >
                  <Home size={18} className="mr-2" /> Convert to Property
                </Button>
              )}
              {offer.status === 'pending' && (
                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 py-3"
                  onClick={() => onWithdraw(offer._id)}
                >
                  <X size={16} className="mr-2" /> Withdraw Offer
                </Button>
              )}
              <Button variant="outline" className="w-full" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Listing Card for Marketplace
const ListingCard = ({ listing, onMakeOffer, hasOffer, onClick }) => (
  <Card className="overflow-hidden hover:shadow-xl transition-all group cursor-pointer" onClick={onClick} data-testid={`marketplace-listing-${listing._id}`}>
    <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 relative">
      {listing.images?.[0] ? (
        <img src={listing.images[0]} alt={listing.address} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Building2 size={48} className="text-gray-400" />
        </div>
      )}
      <div className="absolute top-3 left-3 flex gap-2">
        {listing.furnished && (
          <span className="px-2 py-1 bg-white/90 text-xs rounded-full">Furnished</span>
        )}
        {listing.utilitiesIncluded && (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Bills Inc.</span>
        )}
      </div>
    </div>
    
    <CardContent className="p-4">
      <div className="mb-2">
        <h3 className="font-semibold text-lg group-hover:text-[#1F3A68] transition-colors">
          {listing.address}
        </h3>
        <p className="text-gray-600 flex items-center gap-1 text-sm">
          <MapPin size={14} /> {listing.city}, {listing.postcode}
        </p>
      </div>
      
      <div className="flex gap-4 text-sm text-gray-500 mb-3">
        <span className="flex items-center gap-1"><Bed size={14} /> {listing.bedrooms} bed</span>
        <span className="flex items-center gap-1"><Bath size={14} /> {listing.bathrooms} bath</span>
        <span className="flex items-center gap-1"><Calendar size={14} /> {listing.minTerm}mo min</span>
      </div>
      
      <p className="text-sm text-gray-600 line-clamp-2 mb-4">{listing.description}</p>
      
      <div className="flex justify-between items-center">
        <div>
          <span className="text-2xl font-bold text-[#1F3A68]">£{listing.monthlyRent}</span>
          <span className="text-gray-500">/month</span>
        </div>
        
        {hasOffer ? (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full" title="Click the card to view your offer status">
            Offer Sent &middot; click to view
          </span>
        ) : (
          <Button
            className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
            onClick={(e) => { e.stopPropagation(); onMakeOffer(); }}
            data-testid={`make-offer-btn-${listing._id}`}
          >
            Make Offer
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export default HostDealMarketplace;
